import { useEffect, useRef } from "react";
import { useNavigate } from "fusion-plugin-react-router";
import * as Sentry from "@sentry/react";

export default function GlobalPaymentsIframe({
  hppUrl,
  paymentJson,
  onSuccess,
  onFailure,
}) {
  const iframeRef = useRef(null);

  const navigate = useNavigate();
  useEffect(() => {
    if (!paymentJson || !hppUrl) return;

    let interval;
    let logListener;

    function tryInit() {
      if (window.RealexHpp) {
        window.RealexHpp.setHppUrl(hppUrl);

        window.RealexHpp.embedded.init(
          "autoload",
          iframeRef.current?.id || "iframeId",
          (response, close) => {
            if (response.RESULT === "00") {
              onSuccess(response);
            } else {
              console.error("Payment failed", response);
              onFailure(response);
            }
            close();
          },
          paymentJson,
          (error) => {
            console.error("Payment initialization error:", error);
            Sentry.captureException(
              new Error(
                `GlobalPayments: ${
                  error && error.message ? error.message : error
                }`
              )
            );
            if (navigate) {
              navigate(
                `/blocked-action?type=iframe-error&heading=${encodeURIComponent(
                  "There was an error with our payment page."
                )}&message=${encodeURIComponent(
                  `Error: ${error}, more information: ${
                    error && error.message ? error.message : ""
                  }`
                )}`
              );
            } else {
              onFailure({
                RESULT: "iframe-error",
                error: "Test iframe error navigation",
              });
            }
          }
        );

        logListener = function(event) {
          if (event && event.detail) {
            const { eventName, error_name, ...rest } = event.detail;
            if (
              eventName === "hpp_iframe_error" &&
              [
                "HANDSHAKE_TIMEOUT_OR_IFRAME_BLOCKED",
                "TIMEOUT_WAITING_FOR_RESULT",
                "SDK_NOT_LOADED",
                "INIT_EXCEPTION",
                "SERVER_VERIFY_FAILED",
              ].includes(error_name)
            ) {
              Sentry.captureMessage(`GlobalPayments SDK error: ${error_name}`, {
                extra: rest,
              });
              if (navigate) {
                navigate(
                  `/blocked-action?type=iframe-error&heading=${encodeURIComponent(
                    "There was an error with our payment page."
                  )}&message=${encodeURIComponent(
                    `Event: ${eventName} on ${error_name}`
                  )}`
                );
              } else {
                onFailure({ RESULT: error_name, ...rest });
              }
            }
          }
        };
        window.addEventListener(
          window.RealexHpp.constants.logEventName,
          logListener
        );
        document.body.classList.add("loaded");
        clearInterval(interval);
      }
    }

    interval = setInterval(tryInit, 100);

    return () => {
      clearInterval(interval);
      document.body.classList.remove("loaded");
      if (window.RealexHpp && logListener) {
        window.removeEventListener(
          window.RealexHpp.constants.logEventName,
          logListener
        );
      }
    };
  }, [hppUrl, paymentJson, onSuccess, onFailure]);

  return (
    <iframe
      ref={iframeRef}
      id="iframeId"
      frameBorder="0"
      width="100%"
      height="710px"
      style={{ display: "none", overflowY: "hidden" }}
      sandbox="allow-scripts allow-forms allow-same-origin"
    />
  );
}
